/**
 * StoryPlanWidget — dashboard widget showing memoir story plan progress.
 *
 * Fetches story topics on mount. Shows:
 * - Empty state with CTA to /stories
 * - Progress bar + per-area accordion when topics exist
 * - "Edit story plan" link → /stories
 *
 * Loading: animate-pulse skeleton. Error: silent (widget hidden).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchStoryTopics, type StoryTopic } from '../../lib/story-topics-api';
import { fetchCurriculum, type CurriculumArea } from '../../lib/curriculum-api';

interface AreaWithTopics {
  area: CurriculumArea;
  topics: StoryTopic[];
}

function StatusDot({ status }: { status: StoryTopic['status'] }) {
  const cls =
    status === 'complete'
      ? 'w-2 h-2 rounded-full bg-green-500 shrink-0'
      : status === 'captured'
        ? 'w-2 h-2 rounded-full bg-amber-400 shrink-0'
        : 'w-2 h-2 rounded-full bg-stone-300 shrink-0';
  return <span className={cls} aria-label={status} />;
}

export function StoryPlanWidget() {
  const navigate = useNavigate();
  const [areasWithTopics, setAreasWithTopics] = useState<AreaWithTopics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedAreaIds, setExpandedAreaIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [topics, curriculumData] = await Promise.all([
          fetchStoryTopics(),
          fetchCurriculum(),
        ]);

        if (cancelled) return;

        // Group topics by areaId
        const topicMap: Record<string, StoryTopic[]> = {};
        for (const topic of topics) {
          if (!topicMap[topic.areaId]) topicMap[topic.areaId] = [];
          topicMap[topic.areaId].push(topic);
        }

        // Only include areas that have at least one topic
        const result: AreaWithTopics[] = curriculumData.areas
          .filter((area) => (topicMap[area.id] ?? []).length > 0)
          .map((area) => ({ area, topics: topicMap[area.id] ?? [] }));

        setAreasWithTopics(result);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, []);

  // Silent error
  if (error) return null;

  // ── Loading skeleton ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 space-y-4 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="bg-stone-200 rounded h-4 w-32" />
          <div className="bg-stone-200 rounded h-4 w-10" />
        </div>
        <div className="bg-stone-200 rounded-full h-2 w-full" />
        <div className="space-y-2">
          <div className="bg-stone-200 rounded h-4 w-48" />
          <div className="bg-stone-200 rounded h-4 w-40" />
        </div>
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────────────────

  if (areasWithTopics.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 space-y-3">
        <h2 className="text-base font-semibold text-stone-900">Your Story Plan</h2>
        <p className="text-sm text-stone-500">You haven&rsquo;t planned your memoir yet.</p>
        <button
          type="button"
          onClick={() => navigate('/stories')}
          className="text-sm text-amber-600 font-semibold hover:underline"
        >
          Plan Your Stories →
        </button>
      </div>
    );
  }

  // ── Progress stats ───────────────────────────────────────────────────────────

  const allTopics = areasWithTopics.flatMap(({ topics }) => topics);
  const capturedCount = allTopics.filter(
    (t) => t.status === 'captured' || t.status === 'complete',
  ).length;
  const totalCount = allTopics.length;
  const allCaptured = totalCount > 0 && capturedCount === totalCount;
  const pct = totalCount > 0 ? Math.round((capturedCount / totalCount) * 100) : 0;

  function toggleArea(areaId: string) {
    setExpandedAreaIds((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-stone-900">Your Story Plan</h2>
        <button
          type="button"
          onClick={() => navigate('/stories')}
          className="text-sm text-amber-600 font-semibold hover:underline"
        >
          Edit
        </button>
      </div>

      {/* All-captured celebration banner */}
      {allCaptured ? (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800 font-medium">
          Your memoir is complete!
        </div>
      ) : (
        <>
          {/* Progress bar */}
          <div>
            <p className="text-sm text-stone-600 mb-2">
              {capturedCount} of {totalCount} {totalCount === 1 ? 'story' : 'stories'} captured
            </p>
            <div
              className="w-full bg-stone-200 rounded-full h-2"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${capturedCount} of ${totalCount} stories captured`}
            >
              <div
                className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </>
      )}

      {/* Per-area accordion */}
      <div className="space-y-1">
        {areasWithTopics.map(({ area, topics }) => {
          const isExpanded = expandedAreaIds.has(area.id);
          return (
            <div key={area.id} className="border border-stone-100 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => toggleArea(area.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-stone-50 transition-colors"
              >
                <span className="text-sm font-medium text-stone-800">
                  <span className="mr-1.5 text-stone-400">{isExpanded ? '▾' : '▸'}</span>
                  {area.title}
                </span>
                <span className="text-xs text-stone-500 ml-2 shrink-0">
                  {topics.length} {topics.length === 1 ? 'story' : 'stories'}
                </span>
              </button>

              {isExpanded && (
                <ul className="px-4 pb-3 space-y-2 border-t border-stone-100">
                  {topics.map((topic) => (
                    <li key={topic.id} className="flex items-center gap-2 pt-2">
                      <StatusDot status={topic.status} />
                      <span className="text-sm text-stone-700">{topic.title}</span>
                      <span className="ml-auto text-xs text-stone-400 capitalize">{topic.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
